import { createContext, useContext, useRef, useSyncExternalStore } from 'react';

/**
 * Note that these sets are only ever added to, never removed from.
 * In testing, we found that there's no meaningful performance hit if we never un-render cells.
 */
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * 11 is the legacy value.
 *
 * We hope to set this to 25 in the future.
 * Amplitude shows that the majority of users use page size 25.
 * For them, always showing the first 25 rows would feel snappier.
 * 25 also is generally enough to fill the screen,
 * so larger page sizes should also feel perceived performance improvements.
 */
export const ALWAYS_VISIBLE_ROW_COUNT = 11;

/**
 * 11 is the legacy value.
 *
 * We hope to se this to `Math.ceil(3440 / DEFAULT_COLUMN_WIDTH)`;
 * According to Amplitude, 95% of users have a screen width of 3440px or less.
 * Dividing by the default column width roughly gives us the number of columns that can fit on the screen.
 * Always rendering these should help improve perceived performance.
 */
const ALWAYS_VISIBLE_COLUMN_COUNT = 11;

/**
 * By putting this in a getter, we ensure new objects are being made for each call
 */
const getLocalMutationsStoreDefaultState = () => ({
  visibleRowIndexes: new Set(Array.from({
    length: ALWAYS_VISIBLE_ROW_COUNT
  }, (_, i) => i)),
  visibleColumnIndexes: new Set(Array.from({
    length: ALWAYS_VISIBLE_COLUMN_COUNT
  }, (_, i) => i))
});
const createVirtualizationStore = () => {
  /** We're able to subscribe to specific rows, so the key here is the row index ... */
  const rowSubscribers = new Map();

  /** ... but we can't subscribe to specific columns, so we have to listen to all all of them */
  const allColumnsSubscribers = new Set();
  let state = getLocalMutationsStoreDefaultState();
  let rafPending = false;
  const pendingNotifications = new Set();
  const scheduleNotify = subscriber => {
    pendingNotifications.add(subscriber);
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => {
      rafPending = false;
      pendingNotifications.forEach(sub => sub());
      pendingNotifications.clear();
    });
  };
  const notifyRows = newRows => {
    newRows.forEach(rowIndex => {
      const subscribers = rowSubscribers.get(rowIndex);
      if (subscribers) {
        subscribers.forEach(scheduleNotify);
      }
    });
  };
  const notifyColumns = newColumns => {
    if (newColumns.length > 0) {
      allColumnsSubscribers.forEach(scheduleNotify);
    }
  };
  return {
    getSnapshot() {
      return state;
    },
    subscribeToRow({
      rowIndex,
      subscriber
    }) {
      // If row is already visible, no need to subscribe
      // (this includes the always visible rows)
      if (state.visibleRowIndexes.has(rowIndex)) {
        return () => {};
      }
      if (!rowSubscribers.has(rowIndex)) {
        rowSubscribers.set(rowIndex, new Set());
      }
      rowSubscribers.get(rowIndex).add(subscriber);
      return () => {
        var _rowSubscribers$get;
        (_rowSubscribers$get = rowSubscribers.get(rowIndex)) === null || _rowSubscribers$get === void 0 || _rowSubscribers$get.delete(subscriber);
      };
    },
    subscribeToAllColumns(subscriber) {
      allColumnsSubscribers.add(subscriber);
      return () => {
        allColumnsSubscribers.delete(subscriber);
      };
    },
    reset() {
      const rowIndexes = Array.from(state.visibleRowIndexes);
      const columnIndexes = Array.from(state.visibleColumnIndexes);
      state = getLocalMutationsStoreDefaultState();
      notifyRows(rowIndexes);
      notifyColumns(columnIndexes);
    },
    updateVisibleIndexes: ({
      rows,
      columns
    }) => {
      const newRows = Array.from(rows).filter(rowIndex => !state.visibleRowIndexes.has(rowIndex));
      if (newRows.length > 0) {
        state.visibleRowIndexes = new Set([...state.visibleRowIndexes, ...newRows]);
        notifyRows(newRows);
      }
      const newColumns = Array.from(columns).filter(columnIndex => !state.visibleColumnIndexes.has(columnIndex));
      if (newColumns.length > 0) {
        state.visibleColumnIndexes = new Set([...state.visibleColumnIndexes, ...columns]);
        notifyColumns(newColumns);
      }
    }
  };
};
const VirtualizationContext = /*#__PURE__*/createContext(null);
export const ProvideVirtualization = ({
  children
}) => {
  const store = useRef(createVirtualizationStore());
  return /*#__PURE__*/_jsx(VirtualizationContext.Provider, {
    value: store.current,
    children: children
  });
};
const useVirtualizationStore = () => {
  const store = useContext(VirtualizationContext);
  if (store === null) {
    throw new Error('useVirtualizationStore must be used within a VirtualizationProvider');
  }
  return store;
};
export const useIsRowVisible = rowIndex => {
  const store = useVirtualizationStore();
  const isVisible = useSyncExternalStore(onStoreChange => store.subscribeToRow({
    rowIndex,
    subscriber: onStoreChange
  }), () => {
    const state = store.getSnapshot();
    return state.visibleRowIndexes.has(rowIndex);
  });
  return isVisible;
};
export const useVisibleColumnsIfRowVisible = isRowVisible => {
  const store = useVirtualizationStore();
  return useSyncExternalStore(onStoreChange => {
    if (!isRowVisible) {
      // This turns out to be a non-trivial performance optimization
      return () => {};
    }
    return store.subscribeToAllColumns(onStoreChange);
  }, () => store.getSnapshot().visibleColumnIndexes);
};
export const useUpdateVisibleIndexes = () => {
  const store = useVirtualizationStore();
  return store.updateVisibleIndexes;
};
export const useResetVisibleIndexes = () => {
  const store = useVirtualizationStore();
  return store.reset;
};